import Phaser from "phaser";
import type { TextDirection } from "@yjsgames/i18n";
import { mirrorX } from "./mirror.js";
import type { TabBarStyleToken } from "./tokens.js";

/**
 * `SegmentedTabBar` 탭 전환 이벤트 키.
 * - `TabChange`: 탭 셀 `pointerdown` 시 해당 탭 id를 인자로 emit. 활성 탭을 다시 눌러도 emit하므로
 *   호출자가 멱등 판단(같은 id면 무시) + 전환 가드(modal/phase 등)를 책임진다.
 */
export const SEGMENTED_TAB_BAR_EVENT = {
  TabChange: "segmentedTabBar:change",
} as const;

/** 탭 1개 정의 — id(전환 키) + label(표시 텍스트) + optional icon. */
export interface SegmentedTabBarItem<TabId extends string = string> {
  readonly id: TabId;
  readonly label: string;
  /** 탭 아이콘 텍스처 키 (미지정 시 라벨만 표시). activeIconKey/inactiveIconKey 미지정 시 fallback. */
  readonly iconKey?: string;
  /** 활성 상태 아이콘 텍스처 키 (미지정 시 iconKey fallback). */
  readonly activeIconKey?: string;
  /** 비활성 상태 아이콘 텍스처 키 (미지정 시 iconKey fallback). */
  readonly inactiveIconKey?: string;
}

export interface SegmentedTabBarOptions<TabId extends string = string> {
  readonly style: TabBarStyleToken;
  /** 바 전체 가로 폭 — `tabs.length`로 균등 분할. */
  readonly width: number;
  /** 각 탭 셀 높이. */
  readonly height: number;
  readonly tabs: ReadonlyArray<SegmentedTabBarItem<TabId>>;
  readonly activeId: TabId;
  /** 텍스트 방향 — RTL이면 탭 셀 순서를 좌우 미러링(첫 탭이 우측). default `"ltr"`(기존 동작 회귀 0). */
  readonly dir?: TextDirection;
}

/**
 * 탭 잠금 배지 시각 — 호출자 주입(DIP, 패키지는 자물쇠 키·색을 모름).
 * iconKey=자물쇠 텍스처, chapterLabel=요구 챕터 숫자 표시, tint=흔들림 시 배경 거부 색.
 */
export interface TabLockVisual {
  readonly iconKey: string;
  readonly chapterLabel: string;
  readonly tint: number;
}

/** 탭 1개의 시각 요소 — `setActiveTab`이 부분 갱신하기 위해 필드로 보관. */
interface TabView<TabId extends string> {
  readonly id: TabId;
  readonly bg: Phaser.GameObjects.NineSlice;
  readonly label: Phaser.GameObjects.Text;
  readonly icon?: Phaser.GameObjects.Image;
  /** 활성 상태 아이콘 텍스처 키 — undefined이면 아이콘 전환 생략. */
  readonly activeIconKey?: string;
  /** 비활성 상태 아이콘 텍스처 키 — undefined이면 아이콘 전환 생략. */
  readonly inactiveIconKey?: string;
  /** 챕터 잠금 상태 — true면 콘텐츠 dim + 자물쇠 배지 표시(setTabLocked). */
  locked: boolean;
  /** 잠금 배지 자물쇠 아이콘 (잠금 시에만 생성). */
  lockIcon?: Phaser.GameObjects.Image;
  /** 잠금 배지 요구 챕터 숫자 (잠금 시에만 생성). */
  lockLabel?: Phaser.GameObjects.Text;
  /** 알림 레드닷 (우상단) — setTabDot(true)일 때만 생성. 새 콘텐츠 알림용. */
  redDot?: Phaser.GameObjects.Arc;
  /** 진행 중인 거부 흔들림 tween (counter 기반 — 재호출 시 cancel). */
  shakeTween?: Phaser.Tweens.Tween;
}

function setActiveIfAvailable(obj: Phaser.GameObjects.GameObject | undefined, active: boolean): void {
  (obj as Phaser.GameObjects.GameObject & { setActive?: (value: boolean) => unknown } | undefined)?.setActive?.(
    active,
  );
}

function setVisibleActive<T extends Phaser.GameObjects.GameObject & { setVisible: (visible: boolean) => T }>(
  obj: T | undefined,
  visible: boolean,
): void {
  if (!obj) return;
  obj.setVisible(visible);
  setActiveIfAvailable(obj, visible);
}

/**
 * 제네릭 세그먼트 탭 바 — `TabBarStyleToken` 토큰 기반 (에셋키·레이아웃은 호출자 주입, DIP).
 *
 * `tabs`를 `width` 균등 분할로 배치하고, 각 셀은 NineSlice 배경 + 중앙 정렬 라벨로 구성한다.
 * `dir: "rtl"`이면 셀 순서만 좌우 미러링(셀 내부 아이콘+라벨은 중앙 정렬이라 방향 무관).
 * 셀 `pointerdown` 시 `SEGMENTED_TAB_BAR_EVENT.TabChange`에 탭 id를 emit한다.
 * 활성 표시는 `setActiveTab(id)`로 갱신 — destroy/recreate 없이 텍스처·알파·라벨 스타일·scale만 부분 교체.
 *
 * 하단 탭 / 서브탭 등 "여러 탭 중 하나 활성" UI를 토큰만 바꿔 공유한다.
 *
 * 사용 예:
 * ```ts
 * const bar = new SegmentedTabBar(scene, 0, 1764, {
 *   style: BOTTOM_TAB_BAR_STYLE,
 *   width: 1080, height: 128,
 *   tabs: [{ id: "home", label: "현재" }, { id: "rebirth", label: "환생" }],
 *   activeId: "home",
 * });
 * bar.on(SEGMENTED_TAB_BAR_EVENT.TabChange, (id: "home" | "rebirth") => scene.onTab(id));
 * scene.add.existing(bar);
 * ```
 */
export class SegmentedTabBar<TabId extends string = string> extends Phaser.GameObjects.Container {
  private readonly tabViews: TabView<TabId>[] = [];
  private readonly style: TabBarStyleToken;
  private activeId: TabId;
  /** relayout(방향 전환)에서 셀 좌표를 재계산하기 위한 배치 상수 보관. */
  private readonly barWidth: number;
  private readonly tabWidth: number;
  private readonly barHeight: number;
  private labelsVisible = true;
  /** 마지막 배치 방향 — setTabLocked가 배지 위치를 재계산할 때 사용(positionTabs가 갱신). */
  private currentDir: TextDirection = "ltr";

  /** 비활성 탭 배경 알파 default — 어두운 배경에서도 라벨이 읽히도록 0.8 (활성 1.0과 차별). */
  private static readonly DEFAULT_INACTIVE_ALPHA = 0.8;
  /** 활성 탭 라벨 살짝 확대 default — 선택 상태 강조. */
  private static readonly DEFAULT_ACTIVE_LABEL_SCALE = 1.04;
  /** 잠금 배지는 버튼 높이를 크게 쓰는 아이콘+요구 챕터 숫자 그룹으로 표시한다. */
  private static readonly LOCK_BADGE_ICON_SCALE = 0.68;
  private static readonly LOCK_BADGE_LABEL_SCALE = 1.3;
  private static readonly LOCK_BADGE_GAP = 5;
  private static readonly LOCK_BADGE_LABEL_COLOR = "#fff7d6";
  private static readonly LOCK_BADGE_LABEL_STROKE = "#2a0b0f";
  private static readonly LOCK_BADGE_LABEL_STROKE_THICKNESS = 4;
  private static readonly LOCKED_CONTENT_ALPHA = 0.52;

  constructor(scene: Phaser.Scene, x: number, y: number, options: SegmentedTabBarOptions<TabId>) {
    super(scene, x, y);
    this.style = options.style;
    this.activeId = options.activeId;

    const { tabs, width, height, style } = options;
    const dir = options.dir ?? "ltr";
    const tabWidth = width / tabs.length;
    const border = style.border;
    this.barWidth = width;
    this.tabWidth = tabWidth;
    this.barHeight = height;

    /** 아이콘 표시 크기 — 탭 높이의 절반(소수점 절삭). 라벨 간격(iconGap)은 positionTabs에서 계산. */
    const iconDisplaySize = Math.floor(height * 0.5);

    tabs.forEach((tab) => {
      // 좌표는 positionTabs(dir)가 배치 — 여기선 생성 + 등록만. 초기 텍스처는 inactive(applyActiveStates가 승격).
      const bg = scene.add.nineslice(
        0,
        0,
        style.inactiveTextureKey,
        undefined,
        tabWidth,
        height,
        border.left,
        border.right,
        border.top,
        border.bottom,
      );
      bg.setOrigin(0, 0);
      bg.setInteractive({ useHandCursor: true });
      bg.on("pointerdown", () => this.emit(SEGMENTED_TAB_BAR_EVENT.TabChange, tab.id));
      this.add(bg);

      const activeIconKey = tab.activeIconKey ?? tab.iconKey;
      const inactiveIconKey = tab.inactiveIconKey ?? tab.iconKey;
      const initialIconKey = tab.iconKey ?? (tab.id === this.activeId ? activeIconKey : inactiveIconKey);
      let icon: Phaser.GameObjects.Image | undefined;
      if (initialIconKey) {
        icon = scene.add.image(0, 0, initialIconKey).setDisplaySize(iconDisplaySize, iconDisplaySize);
        this.add(icon);
      }

      const label = scene.add.text(0, 0, tab.label, style.labelStyle).setOrigin(0.5, 0.5);
      this.add(label);

      this.tabViews.push({ id: tab.id, bg, label, icon, activeIconKey, inactiveIconKey, locked: false });
    });

    this.positionTabs(dir);
    this.applyActiveStates();
  }

  /**
   * 탭 셀 좌표 배치 — dir에 따라 셀 순서를 mirrorX로 미러(RTL=첫 탭 우측). 생성자와 relayout이 공유.
   * 셀 내부 아이콘+라벨은 그룹 중앙 정렬이라 방향 무관(좌표만 셀 위치 따라감).
   */
  private positionTabs(dir: TextDirection): void {
    this.currentDir = dir;
    const iconDisplaySize = Math.floor(this.barHeight * 0.5);
    const iconGap = 3;
    this.tabViews.forEach((view, idx) => {
      const tabX = mirrorX(idx * this.tabWidth, this.tabWidth, this.barWidth, dir);
      const cx = tabX + this.tabWidth / 2;
      const cy = this.barHeight / 2;
      view.bg.setPosition(tabX, 0);
      if (view.icon) {
        if (this.labelsVisible) {
          const groupW = iconDisplaySize + iconGap + view.label.width;
          const groupLeft = cx - groupW / 2;
          view.icon.setPosition(groupLeft + iconDisplaySize / 2, cy);
          view.label.setPosition(groupLeft + iconDisplaySize + iconGap + view.label.width / 2, cy);
        } else {
          view.icon.setPosition(cx, cy);
          view.label.setPosition(cx, cy);
        }
      } else {
        view.label.setPosition(cx, cy);
      }
      // 잠금 배지 — 자물쇠 아이콘과 요구 챕터 숫자 그룹을 셀 중앙에 둔다.
      if (view.lockIcon) {
        const lockSize = Math.max(9, Math.floor(this.barHeight * SegmentedTabBar.LOCK_BADGE_ICON_SCALE));
        const labelW = (view.lockLabel?.width ?? 0) * SegmentedTabBar.LOCK_BADGE_LABEL_SCALE;
        const groupW = lockSize + (labelW > 0 ? SegmentedTabBar.LOCK_BADGE_GAP + labelW : 0);
        const groupLeft = cx - groupW / 2;
        view.lockIcon
          .setDisplaySize(lockSize, lockSize)
          .setPosition(groupLeft + lockSize / 2, cy);
        view.lockLabel
          ?.setPosition(
            groupLeft + lockSize + SegmentedTabBar.LOCK_BADGE_GAP + labelW / 2,
            cy,
          )
          .setScale(SegmentedTabBar.LOCK_BADGE_LABEL_SCALE);
      }
      // 알림 레드닷 — 셀 우상단 모서리 (테두리 안쪽).
      if (view.redDot) {
        const m = view.redDot.radius + 6;
        view.redDot.setPosition(tabX + this.tabWidth - m, m);
      }
    });
  }

  /** 방향 전환 — 셀 순서를 미러로 재배치(destroy/recreate 0). RTL 인플레이스 전환용. */
  relayout(dir: TextDirection): void {
    this.positionTabs(dir);
  }

  override setVisible(visible: boolean): this {
    super.setVisible(visible);
    (this as Phaser.GameObjects.GameObject & { setActive?: (value: boolean) => unknown }).setActive?.(
      visible,
    );
    for (const view of this.tabViews) this.setTabInputEnabled(view, visible);
    for (const view of this.tabViews) this.syncViewActivity(view);
    return this;
  }

  /**
   * 탭 셀 중심의 월드 좌표를 반환 — fly-to 연출 등 외부에서 특정 탭 버튼을 타겟으로 삼을 때 사용.
   *
   * Container 로컬 좌표(`bg.x`) + 컨테이너 자체 위치(`this.x/y`)를 합산하여 Scene 월드 좌표로 변환한다.
   * 부모 Container 중첩 구조에서는 월드 변환이 불완전할 수 있으므로 직속 Scene 자식 용도에 적합.
   *
   * @param id 대상 탭의 id.
   * @returns 탭 버튼 중심의 `{x, y}` 월드 좌표. 존재하지 않는 id면 `null`.
   */
  tabWorldCenter(id: TabId): { x: number; y: number } | null {
    const view = this.tabViews.find((v) => v.id === id);
    if (!view) return null;
    return {
      x: this.x + view.bg.x + this.tabWidth / 2,
      y: this.y + view.bg.y + this.barHeight / 2,
    };
  }

  /**
   * 활성 탭 갱신 — 호출자가 탭 전환 승인 직후 호출. activeId가 동일해도 안전(멱등).
   * destroy/recreate 없이 텍스처·알파·라벨 스타일·scale만 교체한다.
   *
   * @param activeId 새롭게 활성화할 탭의 id.
   */
  setActiveTab(activeId: TabId): void {
    if (this.activeId === activeId) return;
    this.activeId = activeId;
    this.applyActiveStates();
  }

  /**
   * 탭 라벨 일괄 갱신 — 언어 변경 등으로 표시 텍스트만 교체한다. destroy/recreate 0건(`setText`만).
   * @param labelOf 탭 id → 새 라벨 문자열.
   */
  setTabLabels(labelOf: (id: TabId) => string): void {
    for (const view of this.tabViews) view.label.setText(labelOf(view.id));
    this.positionTabs(this.currentDir);
  }

  /**
   * 라벨 렌더 표시 여부 — 저사양/아이콘 전용 탭바에서 Text 렌더 비용을 줄인다.
   * 라벨을 숨기면 아이콘은 탭 셀 중앙으로 재배치된다.
   */
  setLabelsVisible(visible: boolean): void {
    if (this.labelsVisible === visible) return;
    this.labelsVisible = visible;
    for (const view of this.tabViews) setVisibleActive(view.label, visible);
    this.positionTabs(this.currentDir);
    for (const view of this.tabViews) this.syncViewActivity(view);
  }

  /** 현재 activeId 기준으로 모든 탭의 active/inactive 시각을 반영. */
  private applyActiveStates(): void {
    const inactiveAlpha = this.style.inactiveAlpha ?? SegmentedTabBar.DEFAULT_INACTIVE_ALPHA;
    const activeLabelScale = this.style.activeLabelScale ?? SegmentedTabBar.DEFAULT_ACTIVE_LABEL_SCALE;
    const activeLabelStyle = this.style.labelActiveStyle ?? this.style.labelStyle;
    for (const view of this.tabViews) {
      const active = view.id === this.activeId;
      view.bg.setTexture(active ? this.style.activeTextureKey : this.style.inactiveTextureKey);
      view.bg.setAlpha(active ? 1.0 : inactiveAlpha);
      view.label.setStyle(active ? activeLabelStyle : this.style.labelStyle);
      view.label.setScale(active ? activeLabelScale : 1.0);
      // 활성/비활성 아이콘 텍스처 전환 — activeIconKey/inactiveIconKey가 다르면 텍스처 교체.
      if (view.icon && view.activeIconKey && view.inactiveIconKey) {
        view.icon.setTexture(active ? view.activeIconKey : view.inactiveIconKey);
      }
      // 잠금 탭도 원래 라벨/아이콘의 위치는 유지하고, 콘텐츠만 살짝 후퇴시킨다.
      const contentAlpha = view.locked ? SegmentedTabBar.LOCKED_CONTENT_ALPHA : active ? 1.0 : inactiveAlpha;
      view.label
        .setAlpha(view.locked ? SegmentedTabBar.LOCKED_CONTENT_ALPHA : 1.0)
        .setVisible(this.labelsVisible);
      view.icon?.setAlpha(contentAlpha);
      view.lockIcon?.setAlpha(1.0);
      view.lockLabel?.setAlpha(1.0);
      this.syncViewActivity(view);
    }
  }

  /**
   * 탭 잠금 상태 전환 — locked=true면 콘텐츠를 dim하고 셀에 자물쇠+요구 챕터 숫자 배지를 띄운다.
   * locked=false면 배지를 destroy하고 콘텐츠 알파를 복구한다. 배지 시각(자물쇠 키·색·숫자)은 호출자 주입(DIP).
   * @param id 대상 탭 id. @param locked 잠금 여부. @param visual 잠금 시 배지 시각(해제 시 무시).
   */
  setTabLocked(id: TabId, locked: boolean, visual?: TabLockVisual): void {
    const view = this.tabViews.find((v) => v.id === id);
    if (!view) return;
    view.locked = locked;
    if (locked && visual) {
      const lockSize = Math.max(9, Math.floor(this.barHeight * SegmentedTabBar.LOCK_BADGE_ICON_SCALE));
      if (!view.lockIcon) {
        view.lockIcon = this.scene.add
          .image(0, 0, visual.iconKey)
          .setDisplaySize(lockSize, lockSize)
          .setTint(visual.tint);
        this.add(view.lockIcon);
      } else {
        view.lockIcon.setTexture(visual.iconKey).setDisplaySize(lockSize, lockSize).setTint(visual.tint);
      }
      if (!view.lockLabel) {
        view.lockLabel = this.scene.add
          .text(0, 0, visual.chapterLabel, this.lockLabelStyle())
          .setOrigin(0.5, 0.5)
          .setScale(SegmentedTabBar.LOCK_BADGE_LABEL_SCALE)
          .clearTint();
        this.add(view.lockLabel);
      } else {
        view.lockLabel
          .setText(visual.chapterLabel)
          .setStyle(this.lockLabelStyle())
          .setScale(SegmentedTabBar.LOCK_BADGE_LABEL_SCALE)
          .clearTint();
      }
      this.positionTabs(this.currentDir);
      this.syncViewActivity(view);
    } else if (!locked) {
      view.lockIcon?.destroy();
      view.lockIcon = undefined;
      view.lockLabel?.destroy();
      view.lockLabel = undefined;
    }
    this.applyActiveStates();
  }

  /**
   * 알림 레드닷 표시/숨김 — show=true면 셀 우상단에 작은 원 1개(새 콘텐츠 알림), false면 destroy.
   * 색은 호출자 주입(DIP, 기본 빨강). 잠금 배지와 독립이라 동시 표시 가능.
   * @param id 대상 탭 id. @param show 표시 여부. @param color 점 색(미지정 시 기본 빨강).
   */
  setTabDot(id: TabId, show: boolean, color = 0xff3b30): void {
    const view = this.tabViews.find((v) => v.id === id);
    if (!view) return;
    if (show) {
      const r = Math.max(4, Math.floor(this.barHeight * 0.09));
      if (!view.redDot) {
        view.redDot = this.scene.add.circle(0, 0, r, color);
        this.add(view.redDot);
      } else {
        view.redDot.setFillStyle(color);
      }
      this.positionTabs(this.currentDir); // 위치 항상 최신화 — relayout(방향 전환) 후 재표시 시 좌표 desync 방지(W-2).
      this.syncViewActivity(view);
    } else {
      view.redDot?.destroy();
      view.redDot = undefined;
    }
  }

  /**
   * 거부 흔들림 — 셀(배경·아이콘·라벨·잠금배지)을 좌우로 흔들고 배경을 tint로 칠한다(±6px sin 3사이클 360ms).
   * 잠긴 탭을 눌렀을 때 "안 된다" 신호. 색은 호출자 소유(NineSliceButton.playDenyFx 동형). 진행 중 재호출은 복원 후 재시작.
   * @param id 대상 탭 id. @param tint 흔들림 동안 배경에 입힐 거부 색.
   */
  shakeTab(id: TabId, tint: number): void {
    const view = this.tabViews.find((v) => v.id === id);
    if (!view) return;
    this.cancelShake(view);
    const offsetPx = 6;
    const els = [view.bg, view.icon, view.label, view.lockIcon, view.lockLabel].filter(
      (e): e is Phaser.GameObjects.NineSlice | Phaser.GameObjects.Image | Phaser.GameObjects.Text =>
        !!e,
    );
    const baseXs = els.map((e) => e.x);
    view.bg.setTint(tint);
    view.shakeTween = this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 360,
      ease: "Linear",
      onUpdate: (tw) => {
        const off = Math.sin((tw.getValue() ?? 0) * Math.PI * 6) * offsetPx;
        els.forEach((e, i) => (e.x = baseXs[i]! + off));
      },
      onComplete: () => {
        els.forEach((e, i) => (e.x = baseXs[i]!));
        view.bg.clearTint();
        view.shakeTween = undefined;
      },
    });
  }

  /** 진행 중 흔들림 중단 + tint 복원 + 셀 위치 재정렬 (재호출·destroy 공용). */
  private cancelShake(view: TabView<TabId>): void {
    if (!view.shakeTween) return;
    view.shakeTween.remove();
    view.shakeTween = undefined;
    view.bg.clearTint();
    this.positionTabs(this.currentDir);
  }

  private setTabInputEnabled(view: TabView<TabId>, enabled: boolean): void {
    if (view.bg.input) {
      view.bg.input.enabled = enabled;
      return;
    }
    if (enabled) view.bg.setInteractive({ useHandCursor: true });
  }

  private lockLabelStyle(): Phaser.Types.GameObjects.Text.TextStyle {
    return {
      ...this.style.labelStyle,
      color: SegmentedTabBar.LOCK_BADGE_LABEL_COLOR,
      fontStyle: "700",
      stroke: SegmentedTabBar.LOCK_BADGE_LABEL_STROKE,
      strokeThickness: SegmentedTabBar.LOCK_BADGE_LABEL_STROKE_THICKNESS,
    };
  }

  private syncViewActivity(view: TabView<TabId>): void {
    const barVisible = this.visible !== false;
    setActiveIfAvailable(view.bg, barVisible);
    setActiveIfAvailable(view.icon, barVisible && view.icon?.visible !== false);
    setActiveIfAvailable(view.label, barVisible && view.label.visible !== false);
    setActiveIfAvailable(view.lockIcon, barVisible && view.lockIcon?.visible !== false);
    setActiveIfAvailable(view.lockLabel, barVisible && view.lockLabel?.visible !== false);
    setActiveIfAvailable(view.redDot, barVisible && view.redDot?.visible !== false);
  }
}
